// Stealth-Agent FE — Devices control panel. List all monitored devices with
// live status + per-device Pause / Resume / Kill (decommission) + interval change.
// antd Table/Tag/Button/Popconfirm/Select. Backend mechanism: /control -> agent
// honors is_tracking_active / is_decommissioned on its next heartbeat (~60s).
import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Popconfirm, Select, message, Tooltip, Input, InputNumber, Popover, Modal, Spin, Descriptions } from 'antd';
import { Monitor, Pause, Play, Power, RefreshCw, Clock, UploadCloud, CheckCircle, XCircle, AlertCircle, ShieldCheck, Upload, Search, Wifi, WifiOff, PauseCircle, Trash2, Download, FileText, Camera, Activity, HelpCircle } from 'lucide-react';
import { getDevices, exportDevicesCsv, control, pauseAll, rollout, getReports, getReport, getLiveDiag } from '../api/client';
import { relativeTime, isOnline, deviceDisplayName, deviceActivityState, lastReportSummary, heartbeatReportGapMs, fmtTime, fmtDuration, formatAgentVersion } from './utils';
import { Kpi, RevealGroup } from './ui';

const EMPTY_STATS = { total: 0, online: 0, offline: 0, paused: 0, killed: 0, cleaned: 0, active: 0 };

const OS_FILTERS = [
  { value: 'all', label: 'All OS' },
  { value: 'macos', label: 'macOS' },
  { value: 'windows', label: 'Windows' },
  { value: 'linux', label: 'Linux' },
  { value: 'unknown', label: 'Unknown' },
];

function OsGlyph({ os, size = 15 }) {
  if (os === 'macos') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className="text-gray-800 dark:text-gray-100">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-2 1.08-3.15-1.03.05-2.29.69-3.03 1.54-.66.75-1.23 1.96-1.08 3.11 1.14.09 2.3-.58 3.03-1.5z" />
      </svg>
    );
  }
  if (os === 'windows') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className="text-sky-600">
        <path d="M3 5.5L10.5 4.4v7.1H3V5.5zm8.5-1.3L21 2.5v9.1h-9.5V4.2zM3 13.5h7.5v7.1L3 19.5v-6zm8.5 0H21v9.1l-9.5-1.3v-7.8z" />
      </svg>
    );
  }
  if (os === 'linux') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className="text-amber-600">
        <path d="M12.5 2c-.6 0-1.3.5-1.7 1.3-.3.6-.4 1.3-.3 1.9l.1.6c-.9.4-1.6 1.1-2 2-.5 1.1-.5 2.3-.1 3.4-.7.3-1.3.8-1.7 1.5-.5.9-.5 2 0 2.9.2.4.5.7.8 1l-.5 1.7c-.3 1 .1 2.1 1 2.6.3.2.6.3 1 .3.5 0 1-.2 1.3-.6l.6-.8c.4.1.8.1 1.2.1h.4c.4 0 .8 0 1.2-.1l.6.8c.3.4.8.6 1.3.6.4 0 .7-.1 1-.3.9-.5 1.3-1.6 1-2.6l-.5-1.7c.3-.3.6-.6.8-1 .5-.9.5-2 0-2.9-.4-.7-1-1.2-1.7-1.5.4-1.1.4-2.3-.1-3.4-.4-.9-1.1-1.6-2-2l.1-.6c.1-.6 0-1.3-.3-1.9C13.8 2.5 13.1 2 12.5 2zm0 1.5c.1 0 .3.1.4.3.1.2.2.5.1.7l-.2.9-.6.1-.6-.1-.2-.9c-.1-.2 0-.5.1-.7.1-.2.3-.3.4-.3h.6zM9.8 9.2c.5 0 .9.2 1.1.5.2-.1.5-.2.8-.2h.6c.3 0 .6.1.8.2.2-.3.6-.5 1.1-.5.4 0 .7.2.9.5.3.4.3 1 0 1.5l-.1.1c.5.4.8 1 .8 1.7 0 .8-.4 1.5-1.1 1.9l-.2.5c-.1.3-.3.5-.6.5h-.2l-.7-.9c-.3.1-.7.1-1 .1h-.4c-.3 0-.7 0-1-.1l-.7.9h-.2c-.3 0-.5-.2-.6-.5l-.2-.5c-.7-.4-1.1-1.1-1.1-1.9 0-.7.3-1.3.8-1.7l-.1-.1c-.3-.5-.3-1.1 0-1.5.2-.3.5-.5.9-.5z" />
      </svg>
    );
  }
  return <HelpCircle size={size} className="text-gray-400" />;
}

/** Infer OS from diagnostics already on the row (no agent change). */
function inferOsFromDiagnostics(diag) {
  if (!diag || typeof diag !== 'object') return null;
  const path = String(diag.tesseract_path || '').trim();
  if (path) {
    if (/^[A-Za-z]:[\\/]/.test(path) || path.includes('\\') || /\.exe$/i.test(path) || /Program Files/i.test(path) || /AppData/i.test(path)) {
      return 'windows';
    }
    if (path.includes('/Users/') || path.includes('/Library/')) return 'macos';
    if (path.includes('/home/') || path.startsWith('/usr/') || path.startsWith('/opt/')) return 'linux';
  }
  if (
    diag.session_id != null
    || diag.workstation_state != null
    || diag.input_desktop != null
    || diag.is_workstation_locked != null
    || diag.session_beacon_mode != null
    || diag.session_connect_state != null
    || diag.active_console_session_id != null
  ) {
    return 'windows';
  }
  return null;
}

function resolveRowOs(row) {
  if (row?.os && row.os !== 'unknown') return row.os;
  return inferOsFromDiagnostics(row?.diagnostics) || 'unknown';
}

function OsBadge({ os }) {
  const key = ['macos', 'windows', 'linux'].includes(os) ? os : 'unknown';
  const label = { macos: 'macOS', windows: 'Windows', linux: 'Linux', unknown: 'Unknown' }[key];
  const tip = key === 'unknown'
    ? 'No OS signal yet — needs a heartbeat with diagnostics (tesseract path / session info)'
    : label;
  return (
    <Tooltip title={tip}>
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200">
        <OsGlyph os={key} />
        <span>{label}</span>
      </span>
    </Tooltip>
  );
}

const STATUS_FILTERS = [
  { key: '', label: 'All devices', stat: 'total', accent: 'indigo', icon: Monitor },
  { key: 'online', label: 'Online', stat: 'online', accent: 'emerald', icon: Wifi },
  { key: 'offline', label: 'Offline', stat: 'offline', accent: 'sky', icon: WifiOff },
  { key: 'paused', label: 'Paused (resume)', stat: 'paused', accent: 'amber', icon: PauseCircle },
  { key: 'killed', label: 'Killed', stat: 'killed', accent: 'rose', icon: Power },
  { key: 'cleaned', label: 'Cleaned', stat: 'cleaned', accent: 'indigo', icon: Trash2 },
];

const INTERVALS = [60, 120, 300, 600].map((v) => ({ value: v, label: `${v}s` }));
// Idle threshold options (sec of no input before "idle"): 30s / 1m / 2m / 5m (default).
const IDLE_THRESHOLDS = [
  { value: 30, label: '30s' },
  { value: 60, label: '1m' },
  { value: 120, label: '2m' },
  { value: 300, label: '5m' },
];

const HEALTH_CHECKS = [
  {
    key: 'session_beacon_mode',
    label: 'Session beacon',
    desc: (d) => d.session_beacon_mode
      ? 'Session 0 beacon — heartbeat + updates active; waiting for Session 1 full agent (unlock/login). Tracking paused.'
      : 'N/A — full agent running (not beacon mode)',
    isIssue: (d) => d.session_beacon_mode === true,
    renderStatus: (d) => {
      if (d.session_beacon_mode == null || d.session_beacon_mode === undefined) return 'na';
      if (d.session_beacon_mode === true) return 'bad';
      return 'ok';
    },
  },
  {
    key: 'workstation_state',
    label: 'PC state',
    desc: (d) => {
      if (d.workstation_state == null || d.workstation_state === undefined) {
        return 'N/A — old agent without workstation state diagnostics';
      }
      const idle = d.last_input_age_sec != null ? ` Last input ${Math.round(d.last_input_age_sec / 60)}m ago.` : '';
      const wts = d.session_connect_state ? ` WTS: ${d.session_connect_state}.` : '';
      const desk = d.input_desktop ? ` Desktop: ${d.input_desktop}.` : '';
      switch (d.workstation_state) {
        case 'locked':
          return `Screen locked (confidence: ${d.lock_confidence || '?'}). Relaunch waits for unlock.${idle}${wts}`;
        case 'logged_out':
          return `No interactive login — login screen or user signed out. Relaunch waits for next logon.${wts}`;
        case 'logged_in_idle':
          return `Logged in, user idle (away). Tracking works once Session 1 agent is running.${idle}${wts}`;
        case 'logged_in_active':
          return `Logged in and active at desktop.${idle}${wts}${desk}`;
        default:
          return `State: ${d.workstation_state}.${idle}${wts}${desk}`;
      }
    },
    isIssue: (d) => d.workstation_state === 'logged_out' || d.workstation_state === 'locked',
    renderStatus: (d) => {
      if (d.workstation_state == null || d.workstation_state === undefined) return 'na';
      if (d.workstation_state === 'logged_in_active') return 'ok';
      if (d.workstation_state === 'logged_in_idle') return 'ok';
      if (d.workstation_state === 'locked' || d.workstation_state === 'logged_out') return 'bad';
      return 'na';
    },
  },
  {
    key: 'session_ok',
    label: 'Windows session',
    desc: (d) => {
      if (d.session_id == null || d.session_id === undefined) {
        return 'N/A — Mac/Linux agent or old agent without session diagnostics';
      }
      if (d.session_ok === false) {
        const idle = d.last_input_age_sec != null ? ` Last OS input ${Math.round(d.last_input_age_sec / 60)}m ago.` : '';
        const pc = d.workstation_state ? ` PC: ${d.workstation_state.replace(/_/g, ' ')}.` : '';
        return `Session ${d.session_id} (Session 0) — cannot capture keyboard/mouse. Auto-relaunch / Restart retries silently.${idle}${pc}`;
      }
      return `Session ${d.session_id} — interactive desktop; keys and clicks work normally`;
    },
    isIssue: (d) => d.session_id != null && d.session_ok === false,
    renderStatus: (d) => {
      if (d.session_id == null || d.session_id === undefined) return 'na';
      if (d.session_ok === true) return 'ok';
      if (d.session_ok === false) return 'bad';
      return 'na';
    },
  },
  { key: 'input_listeners_ok', label: 'Input hooks',      desc: (d) => d.session_ok === false && d.session_id != null
    ? 'Disabled — agent is in Session 0 (see Windows session above). Restart or wait for auto-relaunch.'
    : 'Keyboard/mouse listeners (pynput) — required to detect activity',
    isIssue: (d) => d.input_listeners_ok === false,
  },
  { key: 'identity_ok',       label: 'Identity linked',    desc: 'company_id + user_id are set — agent can upload reports', isIssue: (d) => d.identity_ok === false },
  { key: 'tesseract_ok',      label: 'OCR (Tesseract)',    desc: (d) => d.tesseract_ok === false && d.tesseract_path
    ? `Pipeline paused (idle) or OCR off — binary at ${d.tesseract_path}`
    : 'Tesseract found + pipeline enabled — context captured when user is active',
    isIssue: (d) => d.tesseract_ok === false && !d.tesseract_path,
  },
  { key: 'autostart_ok',      label: 'Auto-start',         desc: 'Startup entry exists — agent restarts after reboot', isIssue: (d) => d.autostart_ok === false },
  { key: 'python_ok',         label: 'Python ≥ 3.8',       desc: (d) => `Python ${d.python_version || '?'} — 3.8+ required`, isIssue: (d) => d.python_ok === false },
  { key: 'backend_reachable', label: 'Backend reachable',  desc: 'Agent can reach the TeamGrid server', isIssue: (d) => d.backend_reachable === false },
];

function HealthBadge({ diag }) {
  if (!diag) {
    return (
      <Tooltip title="No health data yet — agent will send diagnostics on its next heartbeat (~60s)">
        <span className="inline-flex items-center gap-1 text-xs text-gray-400 cursor-default">
          <AlertCircle size={13} className="text-gray-300" /> No data
        </span>
      </Tooltip>
    );
  }

  const issues = HEALTH_CHECKS.filter((c) => {
    if (typeof c.isIssue === 'function') return c.isIssue(diag);
    if (diag[c.key] !== false) return false;
    if (c.key === 'tesseract_ok' && diag.tesseract_path) return false;
    return true;
  });
  const hasLastError = Boolean(diag.last_error);
  const warnings = diag.tesseract_ok === false && diag.tesseract_path
    ? [{ label: 'OCR paused (idle)', desc: 'Normal when user is idle — OCR resumes on activity' }]
    : [];
  const allOk  = issues.length === 0 && warnings.length === 0 && !hasLastError;

  const content = (
    <div className="space-y-1.5 py-0.5" style={{ minWidth: 280 }}>
      {HEALTH_CHECKS.map((c) => {
        const customStatus = typeof c.renderStatus === 'function' ? c.renderStatus(diag) : null;
        const val = diag[c.key];
        const ok  = customStatus === 'ok' || (customStatus == null && val === true);
        const na  = customStatus === 'na' || (customStatus == null && (val === null || val === undefined));
        const bad = customStatus === 'bad' || (customStatus == null && val === false);
        const idleOcr = c.key === 'tesseract_ok' && val === false && diag.tesseract_path;
        const desc = typeof c.desc === 'function' ? c.desc(diag) : c.desc;
        return (
          <div key={c.key} className="flex items-start gap-2">
            {na  ? <AlertCircle size={14} className="text-gray-300 mt-0.5 shrink-0" />
             : idleOcr ? <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
             : ok ? <CheckCircle size={14} className="text-green-500 mt-0.5 shrink-0" />
             : bad ? <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                  : <AlertCircle size={14} className="text-gray-300 mt-0.5 shrink-0" />}
            <div>
              <div className="text-xs font-medium text-gray-800 dark:text-gray-200 leading-tight">{c.label}</div>
              <div className="text-xs text-gray-400 leading-tight">{desc}</div>
            </div>
          </div>
        );
      })}
      {warnings.map((w) => (
        <div key={w.label} className="flex items-start gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
          <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-xs font-medium text-amber-700 dark:text-amber-400 leading-tight">{w.label}</div>
            <div className="text-xs text-gray-400 leading-tight">{w.desc}</div>
          </div>
        </div>
      ))}
      {diag.last_error && (
        <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
          <div className="text-xs font-medium text-red-600 dark:text-red-400 mb-0.5">Last error</div>
          <div className="text-xs text-gray-600 dark:text-gray-300 break-words leading-snug">{diag.last_error}</div>
          {diag.last_error_at && (
            <div className="text-xs text-gray-400 mt-0.5">{new Date(diag.last_error_at).toLocaleString()}</div>
          )}
        </div>
      )}
      {Array.isArray(diag.log_tail) && diag.log_tail.length > 0 && (
        <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Recent agent log</div>
          <pre className="text-[10px] leading-snug text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words font-mono bg-gray-50 dark:bg-gray-900/50 rounded p-2 max-h-40 overflow-y-auto">
            {[...diag.log_tail].reverse().join('\n')}
          </pre>
        </div>
      )}
      {diag.recorded_at && (
        <div className="text-xs text-gray-300 pt-1 border-t border-gray-100 dark:border-gray-700">
          Last checked: {new Date(diag.recorded_at).toLocaleTimeString()}
        </div>
      )}
    </div>
  );

  return (
    <Popover content={content} title={<span className="flex items-center gap-1"><ShieldCheck size={13} /> Device Health</span>} trigger="click" placement="left">
      <button className="inline-flex items-center gap-1 text-xs cursor-pointer border-0 bg-transparent p-0">
        {allOk
          ? <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600 font-medium">All good</span></>
          : hasLastError
            ? <><XCircle size={14} className="text-red-500" /><span className="text-red-600 font-medium">Error</span></>
            : issues.length
              ? <><XCircle size={14} className="text-red-500" /><span className="text-red-600 font-medium">{issues.length} issue{issues.length > 1 ? 's' : ''}</span></>
              : <><AlertCircle size={14} className="text-amber-500" /><span className="text-amber-600 font-medium">OCR idle</span></>
        }
      </button>
    </Popover>
  );
}

function DeviceReportsModal({ device, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!open || !device?.device_id) return;
    let cancelled = false;
    setLoading(true);
    setSelectedId(null);
    setDetail(null);
    getReports({ device_id: device.device_id, user_id: device.user_id, limit: 20 })
      .then((d) => { if (!cancelled) setReports(d.reports || []); })
      .catch(() => { if (!cancelled) message.error('Failed to load reports'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, device?.device_id, device?.user_id]);

  async function openDetail(id) {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await getReport(id);
      setDetail(d.report || null);
    } catch {
      message.error('Failed to load report detail');
    } finally {
      setDetailLoading(false);
    }
  }

  const title = device ? `Reports — ${deviceDisplayName(device)}` : 'Reports';

  return (
    <Modal
      title={<span className="flex items-center gap-2"><FileText size={16} /> {title}</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
    >
      {device && (
        <p className="text-xs text-gray-500 mb-3">
          {device.email || device.user_id} · {device.device_id}
        </p>
      )}
      <Spin spinning={loading}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
            <div className="px-3 py-2 text-xs font-medium bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              Recent reports ({reports.length})
            </div>
            {reports.length === 0 && !loading ? (
              <div className="p-4 text-sm text-gray-400">No reports for this device.</div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {reports.map((r) => {
                  const active = String(r._id) === String(selectedId);
                  return (
                    <button
                      key={r._id}
                      type="button"
                      onClick={() => openDetail(r._id)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors ${active ? 'bg-indigo-50 dark:bg-gray-800' : ''}`}
                    >
                      <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {fmtTime(r.createdAt || r.timestamp)}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        <span className="font-medium">{r.active_app || '—'}</span>
                        {' · '}{Number(r.keyboard_key_presses) || 0} keys
                        {' · '}{Number(r.mouse_clicks) || 0} clicks
                        {' · '}{Number(r.ocr_word_count) || 0} OCR
                      </div>
                      {(r.window_title || r.tab_url) && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">{r.window_title || r.tab_url}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 min-h-[200px]">
            {!selectedId && (
              <div className="text-sm text-gray-400 flex items-center justify-center h-full min-h-[180px]">
                Select a report to view details
              </div>
            )}
            {selectedId && detailLoading && (
              <div className="flex items-center justify-center min-h-[180px]"><Spin /></div>
            )}
            {selectedId && !detailLoading && detail && (
              <div className="space-y-3">
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Time">{fmtTime(detail.createdAt || detail.timestamp)}</Descriptions.Item>
                  <Descriptions.Item label="App">{detail.active_app || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Window">{detail.window_title || '—'}</Descriptions.Item>
                  <Descriptions.Item label="URL">{detail.tab_url || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Active / Idle">{fmtDuration(detail.time_active_sec)} / {fmtDuration(detail.time_idle_sec)}</Descriptions.Item>
                  <Descriptions.Item label="Keys / Clicks">{Number(detail.keyboard_key_presses) || 0} / {Number(detail.mouse_clicks) || 0}</Descriptions.Item>
                  <Descriptions.Item label="App switches">{Number(detail.app_switches) || 0}</Descriptions.Item>
                  <Descriptions.Item label="OCR words">{Number(detail.ocr_word_count) || 0}</Descriptions.Item>
                  <Descriptions.Item label="OCR confidence">{detail.ocr_confidence_label || detail.ocr_confidence_mean || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Embed">{detail.embed_status || '—'}</Descriptions.Item>
                </Descriptions>
                {detail.ocr_text ? (
                  <div>
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">OCR text</div>
                    <pre className="text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                      {detail.ocr_text}
                    </pre>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">OCR text not available (stored in Qdrant when embedded, or 0 words on this report).</p>
                )}
              </div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
}

// Dropdown with presets + a "Custom…" option that swaps to a number input so the admin
// can type any value. Backend clamps to its safe range (interval 60-3600s, idle 30-7200s),
// so a typed value can never break reporting. `value` in seconds, onApply(seconds).
function SecondsPicker({ value, presets, min, max, width = 96, onApply }) {
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(value);
  if (custom) {
    return (
      <InputNumber
        size="small" autoFocus min={min} max={max} value={draft} style={{ width }}
        formatter={(v) => `${v}s`} parser={(v) => String(v).replace(/\D/g, '')}
        onChange={setDraft}
        onPressEnter={() => { setCustom(false); if (draft) onApply(draft); }}
        onBlur={() => { setCustom(false); if (draft && draft !== value) onApply(draft); }}
      />
    );
  }
  // show the current value as an option even if it's not a preset (custom value)
  const opts = presets.some((p) => p.value === value)
    ? presets : [{ value, label: `${value}s` }, ...presets];
  return (
    <Select
      size="small" value={value} style={{ width }} suffixIcon={<Clock size={12} />}
      options={[...opts, { value: '__custom__', label: 'Custom…' }]}
      onChange={(v) => { if (v === '__custom__') { setDraft(value); setCustom(true); } else onApply(v); }}
    />
  );
}

const PAGE_SIZE_DEFAULT = 20;

export default function Devices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // device_id currently acting on
  const [rolloutVer, setRolloutVer] = useState('');
  const [rollingOut, setRollingOut] = useState(false);
  const [deviceUpdateVer, setDeviceUpdateVer] = useState({}); // device_id -> version string
  const [deviceUpdating, setDeviceUpdating] = useState({}); // device_id -> bool
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [statusFilter, setStatusFilter] = useState('');
  const [osFilter, setOsFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [reportDevice, setReportDevice] = useState(null);
  const [pauseAllBusy, setPauseAllBusy] = useState(false);

  async function doRollout(versionOverride) {
    const version = String(versionOverride ?? rolloutVer).trim();
    if (!version) { message.warning('Enter the update version (e.g. fixv20)'); return; }
    setRollingOut(true);
    try {
      const r = await rollout({ version, scope: 'org' });
      message.success(`Update v${version} queued for ${r.queued} device(s) — applies silently within ~60s`);
      setRolloutVer('');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Rollout failed');
    } finally { setRollingOut(false); }
  }

  function confirmRollout() {
    const version = rolloutVer.trim();
    if (!version) {
      message.warning('Enter the rollout version (e.g. fixv20) — this is not device search');
      return;
    }
    Modal.confirm({
      title: 'Roll out agent update?',
      okText: 'Roll out',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      content: (
        <div className="space-y-2 text-sm">
          <p>Push version <strong>{version}</strong> to <strong>all devices in this org</strong>?</p>
          <p className="text-gray-500">Each online agent will silently download and restart within ~60s. This action is not reversible from the dashboard.</p>
          <p className="text-amber-600 font-medium">This is a fleet update — not the search box below.</p>
        </div>
      ),
      onOk: () => doRollout(version),
    });
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQ(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, osFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const activeOs = osFilter && osFilter !== 'all' ? osFilter : undefined;
      const d = await getDevices({
        q: searchQ || undefined,
        status: statusFilter || undefined,
        os: activeOs,
        limit: pageSize,
        skip: (page - 1) * pageSize,
      });
      // Hard guarantee: only show rows matching selected OS (same logic as OS column).
      const incoming = d.devices || [];
      const rowsOut = activeOs
        ? incoming.filter((r) => resolveRowOs(r) === activeOs)
        : incoming;
      setRows(rowsOut);
      setTotal(activeOs ? (d.total ?? rowsOut.length) : (d.total ?? d.count ?? 0));
      setStats({ ...EMPTY_STATS, ...(d.stats || {}) });
    } catch (e) {
      message.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQ, statusFilter, osFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function doExport() {
    if (!total && !stats.total) {
      message.warning('Nothing to export');
      return;
    }
    setExporting(true);
    try {
      const resp = await exportDevicesCsv({
        q: searchQ || undefined,
        status: statusFilter || undefined,
        os: osFilter && osFilter !== 'all' ? osFilter : undefined,
      });
      const capped = resp.headers['x-export-capped'] === '1';
      const exportTotal = resp.headers['x-export-total'];
      const blob = resp.data;
      const filterParts = [statusFilter, osFilter !== 'all' ? osFilter : ''].filter(Boolean);
      const filterSuffix = filterParts.length ? `-${filterParts.join('-')}` : '';
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stealth-devices${filterSuffix}-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      const countLabel = exportTotal ? `${exportTotal} device${exportTotal === '1' ? '' : 's'}` : 'Devices';
      message.success(
        capped
          ? `Exported ${countLabel} (capped at ${exportTotal})`
          : `Exported ${countLabel}`
      );
    } catch (e) {
      if (e?.response?.status === 404) {
        message.warning('Nothing to export for the current filters');
      } else {
        message.error(e?.response?.data?.error || 'Export failed');
      }
    } finally {
      setExporting(false);
    }
  }

  async function act(row, action, extra = {}) {
    setBusy(row.device_id + action);
    try {
      await control({ company_id: row.company_id, user_id: row.user_id, action, ...extra });
      message.success(
        action === 'pause' ? 'Paused — agent stops within ~60s'
        : action === 'resume' ? 'Resumed'
        : action === 'decommission' ? 'Decommissioned — agent self-stops'
        : action === 'restart_agent' ? 'Restart queued — agent respawns silently within ~60s'
        : action === 'trigger_report' ? 'Report queued — one snapshot within ~60s (works even if idle)'
        : action === 'request_live_diag' ? 'Live check queued — read-only; result in ~60s (shows 1 min only)'
        : action === 'set_idle_threshold' ? 'Idle threshold updated'
        : 'Interval updated'
      );
      load();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Action failed');
    } finally { setBusy(null); }
  }

  /** Read-only remote check: no kill/restart; result ephemeral (~60s). */
  async function runLiveDiag(row) {
    const key = row.device_id + 'request_live_diag';
    setBusy(key);
    try {
      await control({ company_id: row.company_id, user_id: row.user_id, action: 'request_live_diag' });
      message.loading({ content: 'Waiting for agent read-only check (next heartbeat)…', key: 'liveDiag', duration: 0 });
      const started = Date.now();
      let found = null;
      while (Date.now() - started < 90000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const d = await getLiveDiag({ company_id: row.company_id, user_id: row.user_id });
          if (d?.result) {
            found = d;
            break;
          }
        } catch (_e) { /* keep polling */ }
      }
      message.destroy('liveDiag');
      if (!found?.result) {
        message.warning('No live-diag result yet — agent offline or old build without Live check');
        return;
      }
      const ttl = Math.max(5, Number(found.expires_in_sec) || 60);
      Modal.info({
        title: `Live check — ${deviceDisplayName(row)}`,
        width: 640,
        okText: 'Close',
        content: (
          <div className="space-y-2 text-sm">
            <p className="text-amber-600">Ephemeral — auto-clears in ~{ttl}s. Not saved permanently.</p>
            <p className="text-xs text-gray-500">
              Hint: <strong>{found.result.hint || '—'}</strong>
              {' · '}queued reports={found.result.queued_reports ?? '—'}
              {' · '}sending={found.result.queued_sending ?? '—'}
              {' · '}python={found.result.python_process_count ?? '—'}
              {' · '}other monitors={found.result.other_monitor_count ?? '—'}
              {' · '}session={found.result.session_id ?? '—'}
            </p>
            <pre className="text-[11px] max-h-72 overflow-auto bg-gray-50 dark:bg-gray-900 p-3 rounded border border-gray-100 dark:border-gray-800">
              {JSON.stringify(found.result, null, 2)}
            </pre>
            {(found.result.log_tail || []).length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Log tail</div>
                <pre className="text-[10px] max-h-40 overflow-auto bg-gray-50 dark:bg-gray-900 p-2 rounded">
                  {(found.result.log_tail || []).join('\n')}
                </pre>
              </div>
            )}
          </div>
        ),
      });
      // Auto-dismiss awareness: refresh after TTL so UI drops expired blob
      setTimeout(() => { load(); }, (ttl + 2) * 1000);
    } catch (e) {
      message.destroy('liveDiag');
      message.error(e?.response?.data?.error || 'Live check failed');
    } finally {
      setBusy(null);
    }
  }

  async function doPauseAll() {
    setPauseAllBusy(true);
    try {
      const r = await pauseAll();
      const n = r.paused ?? 0;
      message.success(
        n
          ? `Paused ${n} device${n === 1 ? '' : 's'} — agents stop within ~60s`
          : 'No active devices to pause'
      );
      load();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Pause all failed');
    } finally {
      setPauseAllBusy(false);
    }
  }

  function confirmPauseAll() {
    const activeCount = Math.max(0, (stats.active ?? 0) || ((stats.total ?? 0) - (stats.paused ?? 0) - (stats.killed ?? 0) - (stats.cleaned ?? 0)));
    Modal.confirm({
      title: 'Pause all devices?',
      okText: 'Pause all',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      content: (
        <div className="space-y-2 text-sm">
          <p>
            Pause monitoring on <strong>all active devices</strong> in this org
            {activeCount ? <> (~{activeCount})</> : null}?
          </p>
          <p className="text-gray-500">Agents stop tracking within ~60s. Already paused or killed devices are skipped. You can resume devices individually afterward.</p>
        </div>
      ),
      onOk: () => doPauseAll(),
    });
  }

  const columns = [
    {
      title: 'Employee / Device', key: 'who', fixed: 'left', width: 220,
      render: (_, r) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{deviceDisplayName(r)}</div>
          <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
            <Monitor className="w-3 h-3 shrink-0" />
            <span className="truncate">{r.email || r.user_id}</span>
          </div>
          <div className="text-[10px] text-gray-300 dark:text-gray-600 font-mono mt-0.5">{r.device_id?.slice(0, 18)}</div>
        </div>
      ),
    },
    {
      title: 'OS', key: 'os', width: 110,
      render: (_, r) => <OsBadge os={resolveRowOs(r)} />,
    },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_, r) => {
        if (r.is_decommissioned) {
          if (r.deletion_confirmed_at) return <Tag color="default">Cleaned ✓</Tag>;
          return <Tag color="red">Killed</Tag>;
        }
        if (!r.is_tracking_active) return <Tag color="orange">Paused</Tag>;
        return isOnline(r)
          ? <Tag color="green">Online</Tag>
          : <Tag color="default">Offline</Tag>;
      },
    },
    {
      title: <Tooltip title="Whether the user is actively working vs agent-only online (idle)">Activity</Tooltip>,
      key: 'activity', width: 120,
      render: (_, r) => {
        const a = deviceActivityState(r);
        return (
          <Tooltip title={a.tip}>
            <Tag color={a.color}>{a.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Last monitor heartbeat (~every 60s) — agent process is alive">Heartbeat</Tooltip>,
      key: 'heartbeat', width: 110,
      render: (_, r) => (
        <span className="text-xs text-gray-500">{relativeTime(r.last_heartbeat_at || r.last_seen_at)}</span>
      ),
    },
    {
      title: <Tooltip title="Click to view recent reports with OCR, keys, and activity metrics">Last report</Tooltip>,
      key: 'last_report', width: 170,
      render: (_, r) => {
        const s = lastReportSummary(r);
        const gap = heartbeatReportGapMs(r);
        const gapTip = gap != null && gap > 5 * 60 * 1000
          ? `Heartbeat is ${Math.round(gap / 60000)}m newer than last report — user likely idle`
          : null;
        return (
          <Tooltip title={gapTip ? `${gapTip} · Click for report details` : 'Click to view report details'}>
            <button
              type="button"
              onClick={() => setReportDevice(r)}
              className="text-left text-xs leading-snug cursor-pointer border-0 bg-transparent p-0 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <div className="text-gray-700 dark:text-gray-300 font-medium underline decoration-dotted underline-offset-2">
                {s.primary}
              </div>
              {s.secondary && <div className="text-gray-400 mt-0.5">{s.secondary}</div>}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="How often the agent reports (60s–3600s). Pick a preset or type a custom value.">Interval</Tooltip>,
      key: 'interval', width: 110,
      render: (_, r) => (
        <SecondsPicker value={r.report_interval} presets={INTERVALS} min={60} max={3600} width={100}
          onApply={(v) => act(r, 'set_interval', { interval: v })} />
      ),
    },
    {
      title: <Tooltip title="No activity for this long → marked idle (30s–7200s). Preset or custom value.">Idle after</Tooltip>,
      key: 'idle', width: 110,
      render: (_, r) => (
        <SecondsPicker value={r.idle_threshold || 300} presets={IDLE_THRESHOLDS} min={30} max={7200} width={100}
          onApply={(v) => act(r, 'set_idle_threshold', { idle_threshold: v })} />
      ),
    },
    {
      title: <Tooltip title="Reports waiting on device disk — upload may be failing if this stays high">Queued</Tooltip>,
      dataIndex: 'queued_reports', key: 'queued', width: 80,
      render: (v) => (v > 0 ? <Tag color="warning">{v}</Tag> : <span className="text-xs text-gray-400">0</span>),
    },
    {
      title: 'Health', key: 'health', width: 130,
      render: (_, r) => {
        const d = r.diagnostics;
        const sessionHint = d?.session_id != null && d.session_ok === false
          ? <span className="text-xs text-red-500">Session {d.session_id}</span>
          : d?.session_id != null && d.session_ok
            ? <span className="text-xs text-green-600">Session {d.session_id}</span>
            : null;
        return (
          <div className="flex flex-col gap-0.5">
            <HealthBadge diag={r.diagnostics} />
            {sessionHint}
            {d?.last_error && (
              <Tooltip title={d.last_error}>
                <span className="text-xs text-red-500 truncate max-w-[140px] cursor-default">{d.last_error}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Version', key: 'version', width: 130,
      render: (_, r) => {
        if (r.update_pending) return <Tag color="processing">updating → {r.update_target}</Tag>;
        if (r.agent_version) return <Tag color="green">{formatAgentVersion(r.agent_version)}</Tag>;
        return <span className="text-xs text-gray-400">—</span>;
      },
    },
    {
      title: 'Control', key: 'control', fixed: 'right', width: 220,
      render: (_, r) => {
        const ver = deviceUpdateVer[r.device_id] || '';
        const updating = !!deviceUpdating[r.device_id];
        async function doDeviceUpdate() {
          const v = ver.trim();
          if (!v) { message.warning('Enter a version first'); return; }
          setDeviceUpdating((prev) => ({ ...prev, [r.device_id]: true }));
          try {
            await rollout({ version: v, scope: 'org', device_id: r.device_id });
            message.success(`v${v} queued for ${deviceDisplayName(r)} — updates within ~60s`);
            setDeviceUpdateVer((prev) => ({ ...prev, [r.device_id]: '' }));
            load();
          } catch (e) {
            message.error(e?.response?.data?.error || 'Update failed');
          } finally {
            setDeviceUpdating((prev) => ({ ...prev, [r.device_id]: false }));
          }
        }
        return (
          <div className="flex flex-col gap-2">
            {/* Row 1 — primary actions */}
            <div className="flex items-center gap-1">
              {!r.is_decommissioned && (
                r.is_tracking_active ? (
                  <Popconfirm title="Pause this device?" description="Agent will stop tracking within ~60s." okText="Pause" onConfirm={() => act(r, 'pause')}>
                    <Tooltip title="Pause monitoring">
                      <Button size="small" icon={<Pause size={13} />} loading={busy === r.device_id + 'pause'} />
                    </Tooltip>
                  </Popconfirm>
                ) : (
                  <Popconfirm title="Resume this device?" description="Agent will resume tracking within ~60s." okText="Resume" okButtonProps={{ type: 'primary' }} onConfirm={() => act(r, 'resume')}>
                    <Tooltip title="Resume monitoring">
                      <Button size="small" type="primary" icon={<Play size={13} />} loading={busy === r.device_id + 'resume'} />
                    </Tooltip>
                  </Popconfirm>
                )
              )}
              <Popconfirm title="Decommission this device?" description="The agent will permanently self-stop." okText="Kill" okButtonProps={{ danger: true }} onConfirm={() => act(r, 'decommission')}>
                <Tooltip title="Kill (decommission permanently)">
                  <Button size="small" danger icon={<Power size={13} />} loading={busy === r.device_id + 'decommission'} />
                </Tooltip>
              </Popconfirm>
              {!r.is_decommissioned && (
                <Popconfirm title="Restart agent?" description={`Silently respawns for ${deviceDisplayName(r)} within ~60s. No popup on user PC.`} okText="Restart" onConfirm={() => act(r, 'restart_agent')}>
                  <Tooltip title="Silent agent restart (not a PC reboot)">
                    <Button size="small" icon={<RefreshCw size={13} />} loading={busy === r.device_id + 'restart_agent'} />
                  </Tooltip>
                </Popconfirm>
              )}
              {!r.is_decommissioned && r.is_tracking_active && (
                <Popconfirm title="Capture one report now?" description={`Queues a single snapshot for ${deviceDisplayName(r)} on the next heartbeat (~60s).`} okText="Capture" onConfirm={() => act(r, 'trigger_report')}>
                  <Tooltip title="One-shot report + screenshot">
                    <Button size="small" icon={<Camera size={13} />} loading={busy === r.device_id + 'trigger_report'} />
                  </Tooltip>
                </Popconfirm>
              )}
              {!r.is_decommissioned && (
                <Tooltip title="Live check (read-only): queue + python counts. No kill/restart. Result shows ~1 min then clears.">
                  <Button
                    size="small"
                    icon={<Activity size={13} />}
                    loading={busy === r.device_id + 'request_live_diag'}
                    onClick={() => runLiveDiag(r)}
                  />
                </Tooltip>
              )}
            </div>
            {/* Row 2 — per-device version push */}
            <div className="flex items-center gap-1">
              <Input
                size="small" placeholder="version" style={{ width: 110 }}
                value={ver}
                onChange={(e) => setDeviceUpdateVer((prev) => ({ ...prev, [r.device_id]: e.target.value }))}
                onPressEnter={doDeviceUpdate}
              />
              <Popconfirm title="Update this device only?" description={`v${ver || '…'} → ${deviceDisplayName(r)}`} okText="Update" disabled={!ver.trim()} onConfirm={doDeviceUpdate}>
                <Tooltip title="Push version to this device only">
                  <Button size="small" icon={<Upload size={13} />} loading={updating} disabled={!ver.trim()} />
                </Tooltip>
              </Popconfirm>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-5">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Monitor className="w-6 h-6 text-blue-600" /> Devices
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Stealth</span>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitored devices — pause, resume, decommission, or change report interval.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip title="Pause monitoring on all active devices in this org">
            <Button
              icon={<Pause size={14} />}
              onClick={confirmPauseAll}
              loading={pauseAllBusy}
              disabled={pauseAllBusy || !(stats.active ?? 0)}
            >
              Pause all
            </Button>
          </Tooltip>
          <Tooltip title="Export CSV (respects search + status + OS filters)">
            <Button
              icon={<Download size={14} />}
              onClick={doExport}
              loading={exporting}
              disabled={exporting || (!total && !stats.total)}
            >
              Export
            </Button>
          </Tooltip>
          <Button icon={<RefreshCw size={14} />} onClick={load} loading={loading}>Refresh</Button>
        </div>
      </div>

      {/* ── KPI stat cards ── */}
      <RevealGroup className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {STATUS_FILTERS.map(({ key, label, stat, accent, icon: Icon }) => {
          const active = statusFilter === key;
          const count = stats[stat] ?? 0;
          return (
            <button
              key={key || 'all'}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={`text-left rounded-2xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                active ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-900' : ''
              }`}
            >
              <Kpi
                icon={Icon}
                label={label}
                value={count}
                isNumber
                accent={accent}
                sub={key === '' ? `${stats.active ?? 0} actively monitoring` : active ? 'Filtered view' : 'Click to filter'}
              />
            </button>
          );
        })}
      </RevealGroup>

      {/* ── Fleet rollout (hidden by default — uncomment to enable) ── */}
      {/* <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <UploadCloud className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Roll out agent update</span>
        <Input size="small" style={{ width: 220 }} placeholder="rollout version e.g. fixv20" value={rolloutVer}
          onChange={(e) => setRolloutVer(e.target.value)} onPressEnter={(e) => { e.preventDefault(); confirmRollout(); }} disabled={rollingOut} />
        <Button size="small" type="primary" icon={<UploadCloud size={13} />} loading={rollingOut} disabled={!rolloutVer.trim()} onClick={confirmRollout}>Roll out</Button>
        <span className="text-xs text-gray-400 ml-auto">Silent · no popup on user devices</span>
      </div> */}

      {/* ── Device table ── */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">

        {/* Toolbar */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              allowClear
              prefix={<Search size={14} className="text-gray-400" />}
              placeholder="Search name, email, device ID, employee ID, user ID…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ maxWidth: 360 }}
            />
            <Select
              value={osFilter}
              onChange={(v) => setOsFilter(v || 'all')}
              style={{ width: 150 }}
              optionLabelProp="label"
              options={OS_FILTERS.map((o) => ({
                value: o.value,
                label: o.label,
                title: o.label,
              }))}
              optionRender={(opt) => (
                <span className="inline-flex items-center gap-1.5">
                  {opt.value !== 'all' ? <OsGlyph os={opt.value} size={13} /> : <Monitor size={13} className="text-gray-400" />}
                  {opt.label}
                </span>
              )}
            />
            {statusFilter && (
              <Button size="small" onClick={() => setStatusFilter('')}>
                ✕ {STATUS_FILTERS.find((f) => f.key === statusFilter)?.label}
              </Button>
            )}
            {osFilter && osFilter !== 'all' && (
              <Button size="small" onClick={() => setOsFilter('all')}>
                ✕ {OS_FILTERS.find((f) => f.value === osFilter)?.label}
              </Button>
            )}
          </div>
          <span className="text-xs text-gray-400">
            {statusFilter || (osFilter && osFilter !== 'all')
              ? `${total} matching · ${stats.total ?? 0} total`
              : `${stats.total ?? 0} device${(stats.total ?? 0) === 1 ? '' : 's'} connected`}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table
            rowKey="device_id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            size="middle"
            scroll={{ x: 1600 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (t, range) => `${range[0]}–${range[1]} of ${t} devices`,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            }}
          />
        </div>
      </div>

      <p className="text-xs text-gray-400">Note: pause / resume / kill / update take effect on the agent's next heartbeat (~60s).</p>

      <DeviceReportsModal
        device={reportDevice}
        open={Boolean(reportDevice)}
        onClose={() => setReportDevice(null)}
      />
    </div>
  );
}
